import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import SplashScreen from "./components/SplashScreen";
import InstallPrompt from "./components/InstallPrompt";
import PushNotificationInitializer from "./components/PushNotificationInitializer";
import AIConcierge from "./components/AIConcierge";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import EventsPage from "./pages/EventsPage";
import StructuresPage from "./pages/StructuresPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import CheckInPage from "./pages/CheckInPage";
import OnlineCheckin from "./pages/OnlineCheckin";
import PassportPage from "./pages/PassportPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminStructures from "./pages/admin/AdminStructures";
import AdminCheckins from "./pages/admin/AdminCheckins";
import AdminServices from "./pages/admin/AdminServices";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminHouseRules from "./pages/admin/AdminHouseRules";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminItineraries from "./pages/admin/AdminItineraries";
import AdminBookings from "./pages/admin/AdminBookingsRedesign";
import AdminLoyaltyRewards from "./pages/admin/AdminLoyaltyRewards";
import AdminStructure from "./pages/admin/AdminStructure";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminRedemptions from "./pages/admin/AdminRedemptions";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminPlaces from "./pages/admin/AdminPlaces";
import AdminIcal from "./pages/admin/AdminIcal";
import AdminBeds24 from "./pages/admin/AdminBeds24";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminDynamicPricing from "./pages/admin/AdminDynamicPricing";
import AdminFinancialDashboard from "./pages/admin/AdminFinancialDashboard";
import AdminFinancialSettings from "./pages/admin/AdminFinancialSettings";
import AdminFinancialDetail from "./pages/admin/AdminFinancialDetail";
import AdminFinancialTimeline from "./pages/admin/AdminFinancialTimeline";
// FASE 1A + 1B + 1C: Gestione Economica
import EconomicOverview from "./pages/admin/economic/EconomicOverview";
import EconomicIncomes from "./pages/admin/economic/EconomicIncomes";
import EconomicExpenses from "./pages/admin/economic/EconomicExpenses";
import EconomicPrimaNota from "./pages/admin/economic/EconomicPrimaNota";
import EconomicSettings from "./pages/admin/economic/EconomicSettings";
import EconomicInvoices from "./pages/admin/economic/EconomicInvoices";
import EconomicReports from "./pages/admin/economic/EconomicReports";
import AdminMessageTemplates from "./pages/admin/AdminMessageTemplates";
import AdminPricingCalendar from "./pages/admin/AdminPricingCalendar";
import AdminQuotes from "./pages/admin/AdminQuotes";
import AdminWhatsApp from "./pages/admin/AdminWhatsApp";
import AdminWhatsAppTemplates from "./pages/admin/AdminWhatsAppTemplates";
import AdminProfile from "./pages/admin/AdminProfile";
import CleaningCalendar from "./pages/admin/CleaningCalendar";
import GuestsListPage from "./pages/admin/GuestsListPage";
import Guest360Page from "./pages/admin/Guest360Page";
import RevenueIntelligence from "./pages/admin/RevenueIntelligence";
import ConnectivityDiagnostics from "./pages/admin/ConnectivityDiagnostics";
// Automation / Communication Center
import AutomationDashboard from "./pages/admin/automation/AutomationDashboard";
import ProvidersPage from "./pages/admin/automation/ProvidersPage";
import TemplatesPage from "./pages/admin/automation/TemplatesPage";
import RulesPage from "./pages/admin/automation/RulesPage";
import WorkflowAutomationsPage from "./pages/admin/automation/WorkflowAutomationsPage";
import QueuePage from "./pages/admin/automation/QueuePage";
import LogsPage from "./pages/admin/automation/LogsPage";
import SimulationPage from "./pages/admin/automation/SimulationPage";
import AutomationSettingsPage from "./pages/admin/automation/SettingsPage";
import RoutingPage from "./pages/admin/automation/RoutingPage";
import CommunicationDashboardPage from "./pages/admin/automation/CommunicationDashboardPage";
import Beds24StatusPage from "./pages/admin/automation/Beds24StatusPage";
import PublicQuote from "./pages/public/PublicQuote";
import ServicesPage from "./pages/ServicesPage";
import ShopPage from "./pages/ShopPage";
import HouseRulesPage from "./pages/HouseRulesPage";
import AboutPage from "./pages/AboutPage";
import BookingPage from "./pages/BookingPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import SmartNotificationsPage from "./pages/SmartNotificationsPage";
import NotificationTemplatesPage from "./pages/NotificationTemplatesPage";
import ItinerariesPage from "./pages/ItinerariesPage";
import ItineraryDetailPage from "./pages/ItineraryDetailPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MagicLoginPage from "./pages/MagicLoginPage";

import "@/App.css";

function App() {
  const [showSplash, setShowSplash] = useState(true);

  // Controlla se è la prima visita della sessione
  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    if (hasSeenSplash) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem('hasSeenSplash', 'true');
    setShowSplash(false);
  };

  return (
    <AuthProvider>
    <LanguageProvider>
      <PushNotificationInitializer />
      <div className="App">
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/magic-login" element={<MagicLoginPage />} />
            
            {/* Guest routes */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/structures" element={<StructuresPage />} />
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/passport" element={<PassportPage />} />
            <Route path="/checkin" element={<CheckInPage />} />
            <Route path="/checkin/:token" element={<OnlineCheckin />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/house-rules" element={<HouseRulesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/account-settings" element={<AccountSettingsPage />} />
            <Route path="/itineraries" element={<ItinerariesPage />} />
            <Route path="/itineraries/:id" element={<ItineraryDetailPage />} />
            <Route path="/cosa-fare" element={<ItinerariesPage />} />
            
            {/* Public Quote Page (no auth required) */}
            <Route path="/preventivo/:quoteId" element={<PublicQuote />} />
            
            {/* Admin routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/events" element={<AdminEvents />} />
            <Route path="/admin/structures" element={<AdminStructures />} />
            {/* Guest routes - redirect old to new CRM */}
            <Route path="/admin/guests" element={<Navigate to="/admin/crm/guests" replace />} />
            <Route path="/admin/checkins" element={<AdminCheckins />} />
            <Route path="/admin/services" element={<AdminServices />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/house-rules" element={<AdminHouseRules />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/itineraries" element={<AdminItineraries />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/loyalty-rewards" element={<AdminLoyaltyRewards />} />
            <Route path="/admin/structure" element={<AdminStructure />} />
            <Route path="/admin/notifications" element={<AdminNotifications />} />
            <Route path="/admin/redemptions" element={<AdminRedemptions />} />
            <Route path="/admin/media" element={<AdminMedia />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/places" element={<AdminPlaces />} />
            <Route path="/admin/ical" element={<AdminIcal />} />
            <Route path="/admin/beds24" element={<AdminBeds24 />} />
            <Route path="/admin/pricing" element={<AdminPricing />} />
            <Route path="/admin/dynamic-pricing" element={<AdminDynamicPricing />} />
            <Route path="/admin/revenue-intelligence" element={<RevenueIntelligence />} />
            <Route path="/admin/connectivity" element={<ConnectivityDiagnostics />} />
            {/* Push notifications merged into /admin/smart-notifications */}
            <Route path="/admin/smart-notifications" element={<SmartNotificationsPage />} />
            <Route path="/admin/notification-templates" element={<NotificationTemplatesPage />} />
            <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
            <Route path="/admin/whatsapp/templates" element={<AdminWhatsAppTemplates />} />
            <Route path="/admin/profile" element={<AdminProfile />} />
            <Route path="/admin/cleaning-calendar" element={<CleaningCalendar />} />
            
            {/* Communication Center - Phase 5.3A */}
            <Route path="/admin/communication/dashboard" element={<AutomationDashboard />} />
            <Route path="/admin/communication/overview" element={<CommunicationDashboardPage />} />
            <Route path="/admin/communication/channels" element={<ProvidersPage />} />
            <Route path="/admin/communication/templates" element={<TemplatesPage />} />
            <Route path="/admin/communication/rules" element={<RulesPage />} />
            <Route path="/admin/communication/scheduler" element={<AutomationDashboard />} />
            <Route path="/admin/communication/queue" element={<QueuePage />} />
            <Route path="/admin/communication/logs" element={<LogsPage />} />
            <Route path="/admin/communication/simulation" element={<SimulationPage />} />
            <Route path="/admin/communication/routing" element={<RoutingPage />} />
            <Route path="/admin/communication/settings" element={<AutomationSettingsPage />} />
            
            {/* Beds24 Automatic Sync - Phase 5.4 */}
            <Route path="/admin/beds24/status" element={<Beds24StatusPage />} />
            
            {/* Legacy Automation routes - Redirect to Communication Center */}
            <Route path="/admin/automation/dashboard" element={<AutomationDashboard />} />
            <Route path="/admin/automation/providers" element={<ProvidersPage />} />
            <Route path="/admin/automation/templates" element={<TemplatesPage />} />
            <Route path="/admin/automation/rules" element={<RulesPage />} />
            <Route path="/admin/automation/workflows" element={<WorkflowAutomationsPage />} />
            <Route path="/admin/automation/queue" element={<QueuePage />} />
            <Route path="/admin/automation/logs" element={<LogsPage />} />
            <Route path="/admin/automation/simulation" element={<SimulationPage />} />
            <Route path="/admin/automation/settings" element={<AutomationSettingsPage />} />
            
            <Route path="/admin/crm" element={<GuestsListPage />} />
            <Route path="/admin/crm/guests" element={<GuestsListPage />} />
            <Route path="/admin/crm/guests/:guestId/360" element={<Guest360Page />} />
            {/* Legacy route redirects - guestId route still works */}
            <Route path="/admin/guests/:guestId" element={<Guest360Page />} />
            <Route path="/admin/financial" element={<AdminFinancialDashboard />} />
            <Route path="/admin/financial/settings" element={<AdminFinancialSettings />} />
            <Route path="/admin/financial/detail" element={<AdminFinancialDetail />} />
            <Route path="/admin/financial/timeline" element={<AdminFinancialTimeline />} />
            
            {/* FASE 1A + 1B + 1C: Gestione Economica */}
            <Route path="/admin/economic/overview" element={<EconomicOverview />} />
            <Route path="/admin/economic/entrate" element={<EconomicIncomes />} />
            <Route path="/admin/economic/uscite" element={<EconomicExpenses />} />
            <Route path="/admin/economic/prima-nota" element={<EconomicPrimaNota />} />
            <Route path="/admin/economic/fatture" element={<EconomicInvoices />} />
            <Route path="/admin/economic/report" element={<EconomicReports />} />
            <Route path="/admin/economic/impostazioni" element={<EconomicSettings />} />
            <Route path="/admin/message-templates" element={<AdminMessageTemplates />} />
            <Route path="/admin/pricing-calendar" element={<AdminPricingCalendar />} />
            <Route path="/admin/quotes" element={<AdminQuotes />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors offset="60px" />
        <InstallPrompt />
        {/* AIConcierge removed - now integrated directly in HomePage "Oggi a Paestum" section */}
      </div>
    </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
